export type MenuSearchNode = {
  name?: unknown;
  key?: string;
  path?: string;
  children?: MenuSearchNode[];
};

const itemLabel = (name: unknown): string =>
  typeof name === 'string' ? name : '';

const childNodes = <T extends MenuSearchNode>(item: T): T[] =>
  Array.isArray(item.children) ? (item.children as T[]) : [];

/**
 * Keeps menu branches whose title, or a nested title, matches the keyword.
 * An empty keyword returns the original list so the sidebar stays unchanged.
 */
export function filterMenuTree<T extends MenuSearchNode>(
  items: T[] | undefined,
  keyword: string,
): T[] {
  if (!items) return [];

  const query = keyword.trim().toLowerCase();
  if (!query) return items;

  const filterNodes = (nodes: T[]): T[] => {
    const next: T[] = [];

    nodes.forEach((item) => {
      const children = childNodes(item);
      const selfMatches = itemLabel(item.name).toLowerCase().includes(query);

      if (children.length === 0) {
        if (selfMatches) next.push(item);
        return;
      }

      if (selfMatches) {
        next.push(item);
        return;
      }

      const matchedChildren = filterNodes(children);
      if (matchedChildren.length > 0) {
        next.push({ ...item, children: matchedChildren });
      }
    });

    return next;
  };

  return filterNodes(items);
}

/** Keys of groups that should be expanded so matches are visible. */
export function collectSubmenuKeys(
  items: MenuSearchNode[] | undefined,
): string[] {
  const keys: string[] = [];

  const walk = (nodes: MenuSearchNode[]) => {
    nodes.forEach((node) => {
      const children = childNodes(node);
      if (children.length === 0) return;

      const key = node.key || node.path;
      if (key) keys.push(key);
      walk(children);
    });
  };

  if (items?.length) walk(items);
  return keys;
}
